import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AlertPosition, AlertType, TextAlignment } from '../../enum/EnumRepository';
import { TagData } from '../../dto/EntityRepository';
import { DeleteTagReq, UpdateTagReq } from '../../dto/ReqDtoRepository';
import { INPUT_TAG_NAME_MAX_LENGTH } from '../../constants/limits';
import { WorkViewerInfo } from '../headless/input/InputBody';
import { useStandardAlertStore } from '../../store/portal/alertStore';
import { getAlertMessage } from '../../constants/alertMessages';
import { useValidation } from '../../hooks/useValidation';
import HeadlessInput from '../headless/input/HeadlessInput';
import UniversalIconBtn from '../headless/button/UniversalIconBtn';
import confirmIcon from '../../../asset/icon/confirm2.png';
import confirmIcon2 from '../../../asset/icon/confirm3.png';
import deleteOpenIcon from '../../../asset/icon/delete_open.png';
import deleteCloseIcon from '../../../asset/icon/delete_close.png';

interface TagStationTagRowProps {
  tag: TagData;
  updateTag: (data: UpdateTagReq) => Promise<void>;
  deleteTag: (data: DeleteTagReq) => Promise<void>;
  getDeleteConfirmMessage?: (tag: TagData) => string;
  onDirtyChange?: (tagId: number, dirty: boolean) => void;
}

const TagStationTagRow: React.FC<TagStationTagRowProps> = ({
  tag,
  updateTag,
  deleteTag,
  getDeleteConfirmMessage,
  onDirtyChange,
}) => {
  const [draft, setDraft] = useState(tag.name);
  const [error, setError] = useState('');
  const { enqueueAlert } = useStandardAlertStore();
  const { validateTagNameInput } = useValidation();

  const validateDraftInput = (value: string) => {
    if (value.length === 0) {
      setError('');
      return;
    }
    const msg = validateTagNameInput(value);
    if (msg) {
      setError(msg);
      return;
    }
    setError('');
  };

  useEffect(() => {
    setDraft(tag.name);
    setError('');
  }, [tag.id, tag.name]);

  const dirty = draft !== tag.name;

  useEffect(() => {
    onDirtyChange?.(tag.id, dirty);
    return () => {
      onDirtyChange?.(tag.id, false);
    };
  }, [dirty, tag.id, onDirtyChange]);

  const handleSave = async () => {
    if (!draft.trim()) {
      setError(getAlertMessage('MISSING_REQUIRED_FIELD'));
      return;
    }
    if (error !== '') {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('CHECK_FORMAT'),
      });
      return;
    }
    const msg = validateTagNameInput(draft);
    if (msg) {
      setError(msg);
      return;
    }
    const trimmed = draft.trim();
    if (trimmed === tag.name) {
      return;
    }
    await updateTag({ id: tag.id, name: trimmed });
  };

  const handleDelete = () => {
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getDeleteConfirmMessage?.(tag)
        ?? getAlertMessage('CONFIRM_DELETE_TAG', { name: tag.name }),
      callBack: async () => {
        await deleteTag({ tagId: tag.id });
      },
    });
  };

  const handleDraftChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (v.length <= INPUT_TAG_NAME_MAX_LENGTH) {
      setDraft(v);
      validateDraftInput(v);
    }
  };

  return (
    <TagRowBlock>
      <Row>
        <InputWrap>
          <HeadlessInput
            value={draft}
            placeholder="Tag name"
            handleChange={handleDraftChange}
            StyledInput={WorkViewerInfo}
            $alignment={TextAlignment.LEFT}
          />
        </InputWrap>
        <BtnContainer>
          {dirty && (
            <UniversalIconBtn
              isPrimary={true}
              size="fit"
              border={false}
              backgroundImg={confirmIcon}
              backgroundImgHover={confirmIcon2}
              onClick={handleSave}
            />
          )}
            <UniversalIconBtn
              isPrimary={true}
              size="fit"
              border={false}
              backgroundImg={deleteCloseIcon}
              backgroundImgHover={deleteOpenIcon}
              onClick={handleDelete}
            />
        </BtnContainer>
      </Row>
      <ErrorText>{ error !== '' ? error : ''}</ErrorText>
    </TagRowBlock>
  );
};

const TagRowBlock = styled.div`
  width: 100%;
  height: 52px;
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  width: 100%;
  min-height: 22px;
  display: flex;
  align-items: center;
  gap: clamp(8px, 1vw, 12px);
  margin-bottom: 4px;
`;

const InputWrap = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
`;

const BtnContainer = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5vh;
`;

const ErrorText = styled.p`
  width: 100%;
  height: 22px;
  color: ${({ theme }) => theme.colors.color_alert_red};
  ${({ theme }) => theme.typography.Body_04};
`;

export default TagStationTagRow;
